import { showNotification } from '@mantine/notifications';
import { EntityType } from '@repo/common/enums/entityType';
import { pluralize } from '@repo/common/utils';
import type { IconNames } from '@repo/foundations';
import { Icon, Text } from '@repo/foundations';
import { useCallback, useMemo } from 'react';
import type { IBaseModel } from '../../api';
import { queryClient } from '../../api';
import { closeAllModals, openConfirmModal } from '../ModalManager';
import type { ICommandListItem } from '../Spotlight/components/CommandPalette/constants';

export function useDeleteManyAction<T extends IBaseModel>({
	name,
	queryKeys,
	onDelete,
}: {
	name: string;
	queryKeys: readonly string[];
	onDelete: (id: string) => Promise<any>;
}): { action: ICommandListItem<T> } {
	const deleteFn = useCallback(
		async (selected: T[]) => {
			const results = await Promise.allSettled(
				selected.map((el) => onDelete(el.id))
			);
			const success = results.filter((result) => result.status === 'fulfilled');
			const failed = results.filter((result) => result.status === 'rejected');

			if (failed.length > 0) {
				showNotification({
					title: `Error deleting ${failed.length} ${pluralize(name)}`,
					message: 'If the issue persists, please contact support',
					color: 'red',
					icon: (
						<Icon name="alertCircle" color="text/critical-on-fill/default" />
					),
				});
			} else {
				showNotification({
					title: `${success.length} ${pluralize(name)}  deleted`,
					message: `The ${pluralize(name)} have been deleted successfully.`,
					color: 'green',
					icon: <Icon name="check" color="text/success-on-fill/default" />,
				});
			}

			queryClient.invalidateQueries({
				queryKey: queryKeys,
			});
			closeAllModals();
		},
		[onDelete, name, queryKeys]
	);

	const handleDelete = useCallback(
		async (selected: T[], clearSelected: VoidFunction) => {
			openConfirmModal({
				variant: 'default',
				title: `Delete ${selected.length > 1 ? pluralize(name) : name}?`,
				children: <Text>You can&apos;t undo this action.</Text>,
				tone: 'critical',
				confirmLabel: `Delete ${selected.length > 1 ? pluralize(name) : name}`,
				confirmProps: {
					variant: 'primary',
					tone: 'critical',
				},
				onConfirm: async () => {
					await deleteFn(selected);
					clearSelected();
				},
				classNames: {
					body: '',
				},
			});
		},
		[deleteFn, name]
	);

	const action = useMemo(
		() => ({
			id: 'actions::delete',
			title: 'Delete',
			name: 'Delete',
			iconName: 'trash' as IconNames,
			hotkey: '/dq',
			type: EntityType.all,
			team: undefined,
			category: 'actions',
			show: true,
			onClick: handleDelete,
		}),
		[handleDelete]
	);

	return { action };
}
